import { Button } from './ui/button';
import { useProfileState } from '../hooks';
import { useLanguage } from '@/contexts/AppContext';
import {
  ProfileHeader,
  ProfileForm,
  BirthdayPicker,
  GenderSelector,
  AvatarSelectionModal,
  BirthdayConfirmDialog,
  UnsavedChangesDialog,
  profileTranslations,
} from './profile';

interface ProfilePageProps {
  onBack: () => void;
}

export function ProfilePage({
  onBack,
}: ProfilePageProps) {
  const { locale } = useLanguage();
  const {
    // 상태
    isEditing,
    profileData,
    profileImage,
    showAvatarModal,
    showCalendar,
    showBirthdayConfirm,
    tempBirthday,
    isBirthdayLocked,
    showUnsavedChangesDialog,
    isSaving,
    fileInputRef,

    // 상태 설정자
    setProfileData,
    setShowAvatarModal,
    setShowCalendar,
    setShowBirthdayConfirm,
    setTempBirthday,
    setShowUnsavedChangesDialog,

    // 핸들러
    handleImageUpload,
    handleAvatarSelect,
    handleImageClick,
    handleSave,
    handleBackWithCheck,
    handleSaveAndExit,
    handleDiscardChanges,
    handleBirthdayConfirm,
    handleBirthdayCancel,
  } = useProfileState();

  const t = profileTranslations[locale];

  return (
    <div className="min-h-screen bg-muted">
      {/* Profile Header with Avatar */}
      <ProfileHeader
        profileImage={profileImage}
        onBackClick={() => handleBackWithCheck(onBack)}
        onImageClick={handleImageClick}
      />

      {/* Profile Content */}
      <div
        className="px-[min(5vw,1.25rem)] py-[min(6vw,1.5rem)] pb-[min(22vw,7rem)] max-w-[500px] mx-auto w-full"
        style={{ fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif" }}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />

        {/* Profile Form */}
        <ProfileForm
          isEditing={isEditing}
          profileData={profileData}
          setProfileData={setProfileData}
        />

        {/*
          Birthday Picker — admin#1060 Phase 1 머지 완료. menu#275 에서 disabled 제거.
        */}
        <div className="mt-[min(5vw,1.5rem)]">
          <BirthdayPicker
            isEditing={isEditing}
            profileData={profileData}
            showCalendar={showCalendar}
            setShowCalendar={setShowCalendar}
            tempBirthday={tempBirthday}
            setTempBirthday={setTempBirthday}
            isBirthdayLocked={isBirthdayLocked}
            setShowBirthdayConfirm={setShowBirthdayConfirm}
          />
        </div>

        {/* Gender Selector — admin#1060 Phase 1 Gender enum 정합 (menu#275) */}
        <div className="mt-[min(5vw,1.5rem)]">
          <GenderSelector
            isEditing={isEditing}
            profileData={profileData}
            setProfileData={setProfileData}
          />
        </div>

        {/* Save Button — isSaving 시 비활성화 + 저장 중 라벨 (#315 P1-2 시정) */}
        <div className="mt-[min(10vw,3rem)]">
          <Button
            onClick={() => handleSave(onBack, locale)}
            disabled={isSaving}
            className="w-full bg-primary text-white h-[min(13vw,3.25rem)] rounded-[5px] font-medium shadow-md shadow-primary/20 disabled:opacity-60"
          >
            {isSaving ? t.savingProfile : t.save}
          </Button>
        </div>
      </div>

      {/* Avatar Selection Modal */}
      <AvatarSelectionModal
        open={showAvatarModal}
        onOpenChange={setShowAvatarModal}
        onAvatarSelect={handleAvatarSelect}
        onUploadClick={() => fileInputRef.current?.click()}
      />

      {/* Birthday Confirmation Dialog */}
      <BirthdayConfirmDialog
        open={showBirthdayConfirm}
        onOpenChange={setShowBirthdayConfirm}
        tempBirthday={tempBirthday}
        onConfirm={handleBirthdayConfirm}
        onCancel={handleBirthdayCancel}
      />

      {/* Unsaved Changes Confirmation Dialog */}
      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onOpenChange={setShowUnsavedChangesDialog}
        onSaveAndExit={() => handleSaveAndExit(onBack, locale)}
        onDiscardChanges={() => handleDiscardChanges(onBack)}
      />
    </div>
  );
}
